import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { createOrUpdateCampaign, readCampaigns, deleteCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import { ScopeContext } from '@bingads-webui-react/scope-service';

import { usePreference, preferenceNames } from './use-preference';
import { useGetDefaultBudgetValueForCountry } from './use-get-budget-default-value-for-country';

export function usePmaxCampaignCRUD({
  onCreateCampaignDone = _.noop,
  onCreateCampaignError = _.noop,
} = {}) {
  const { scope } = useContext(ScopeContext) as any;

  const [{ value: languagePreference }] = usePreference(preferenceNames.SelectedLanguage);
  const { getDefaultBudgetValueForCountry } = useGetDefaultBudgetValueForCountry();

  const readCampaignsPromise = useCallback(() => {
    const { cid: currentCid, aid: currentAid } = scope.values;
    return readCampaigns(currentCid, currentAid).then((res) => {
      const campaigns = res.value;
      return Promise.resolve(campaigns);
    });
  }, [scope]);

  const deleteCampaignPromise = useCallback((deleteCampaignId) => {
    if (deleteCampaignId) {
      return deleteCampaign(deleteCampaignId).then(() => {
        scope.remove(['campaignId', 'assetGroupId']);
        scope.save(undefined, undefined, { replace: true });
      });
    }

    return Promise.resolve();
  }, [scope]);

  // @aiContributed-MAPFuse-2026-02-05
  const createOrUpdateCampaignPromise = useCallback(
    () => getDefaultBudgetValueForCountry().then((defaultBudgetValue) => {
      const campaignData = {
        Name: `Performance-Max-${Math.floor(Math.random() * 100)}`,
        Budget: {
          Amount: defaultBudgetValue,
          Type: 'DailyStandard',
        },
        Status: 'Active',
        TimeZone: 'PacificTimeUSCanadaTijuana',
        CampaignType: 'PerformanceMax',
        BiddingScheme: {
          Type: 'MaxConversions', // Enhanced CPC will be supported for Performance Max campaigns in Q1
        },
        Languages: [languagePreference],
      };

      let campaignPromise = () => Promise.resolve();
      // Read campaignId from scope.values at execution time to get the latest value
      // (the closure-captured campaignId may be stale after deleteCampaignIfNotMatch runs)
      if (!scope.values.campaignId) {
        campaignPromise = () => createOrUpdateCampaign(campaignData).then((campaign) => {
          scope.append({ campaignId: campaign.Id });
          scope.save(undefined, undefined, { replace: true });
          return campaign;
        });
      }

      return campaignPromise()
        .then((campaign) => {
          onCreateCampaignDone();
          return campaign;
        })
        .catch((error) => {
          onCreateCampaignError(error);
          throw new Error(error);
        });
    }),
    [getDefaultBudgetValueForCountry, languagePreference,
      onCreateCampaignDone, onCreateCampaignError, scope]
  );

  return {
    createOrUpdateCampaignPromise,
    deleteCampaignPromise,
    readCampaignsPromise,
  };
}
